import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  email: string;
  role: 'admin' | 'editor';
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  verify: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load token from localStorage on mount
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const savedToken = localStorage.getItem('cms_token');
      if (savedToken) {
        setToken(savedToken);
        await verifyToken(savedToken).catch(() => {
          localStorage.removeItem('cms_token');
          setToken(null);
        });
      }

      if (isMounted) {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  async function verifyToken(tok: string): Promise<boolean> {
    try {
      const res = await fetch('/api/cms/auth?action=verify', {
        headers: { Authorization: `Bearer ${tok}` },
      });

      if (!res.ok) throw new Error('Token invalid');

      const data = await res.json();
      setUser(data.user);
      return true;
    } catch {
      setUser(null);
      setToken(null);
      return false;
    }
  }

  async function login(email: string, password: string): Promise<void> {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/cms/auth?action=login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Login failed');
      }

      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('cms_token', data.token);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setUser(null);
    setToken(null);
    setError(null);
    localStorage.removeItem('cms_token');
  }

  async function verify(): Promise<boolean> {
    if (!token) return false;
    return verifyToken(token);
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, error, login, logout, verify }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
